const FLAG = '__mypace_chunk_reload';

/**
 * Pathname for emitted hashed bundles (e.g. webpack `[name].[contenthash].js`).
 * Guards SyntaxError HTML-as-JS detection so real JSX/template parse errors do not reload.
 */
const HASHED_CHUNK_PATHNAME = /^\/[\w-]+\.[a-f0-9]{8,}\.(?:js|mjs|css)$/;

function pathnameForChunkFilename(filename) {
  const stripped = filename.split('?')[0] ?? '';
  if (!stripped) return '';
  if (stripped.startsWith('/')) return stripped;
  try {
    const origin =
      typeof window !== 'undefined' && window.location?.origin
        ? window.location.origin
        : 'http://local.invalid';
    return new URL(stripped, origin).pathname;
  } catch {
    return '';
  }
}

/** User-visible / webpack messages when hashed assets 404 after a deploy. */
export function looksLikeChunkFailure(message, filename) {
  if (
    /ChunkLoadError/i.test(message) ||
    /Loading (?:CSS )?chunk \d+ failed/i.test(message) ||
    /Failed to fetch dynamically imported module/i.test(message) ||
    /Importing a module script failed/i.test(message)
  ) {
    return true;
  }
  if (
    filename &&
    HASHED_CHUNK_PATHNAME.test(pathnameForChunkFilename(filename)) &&
    /SyntaxError/i.test(message) &&
    /Unexpected token/i.test(message) &&
    message.includes('<')
  ) {
    return true;
  }
  return false;
}

function messageFromReason(reason) {
  if (reason instanceof Error) {
    return `${reason.name} ${reason.message}`;
  }
  if (typeof reason === 'string') {
    return reason;
  }
  if (
    reason &&
    typeof reason === 'object' &&
    'message' in reason &&
    typeof reason.message === 'string'
  ) {
    return reason.message;
  }
  return String(reason ?? '');
}

/** Single full page reload per tab session when chunk/CSS load fails (avoids loops). */
export function reloadOnceForStaleChunks() {
  try {
    if (sessionStorage.getItem(FLAG)) return;
    sessionStorage.setItem(FLAG, '1');
  } catch {
    return;
  }
  window.location.reload();
}

/** If the failure matches stale hashed-asset patterns, reload once and stop. */
export function tryRecoverStaleChunkError(reason, filename) {
  if (typeof window === 'undefined') return;
  if (looksLikeChunkFailure(messageFromReason(reason), filename)) {
    reloadOnceForStaleChunks();
  }
}

export function installChunkLoadAutoReload() {
  if (typeof window === 'undefined') return;
  window.addEventListener('unhandledrejection', (event) => {
    tryRecoverStaleChunkError(event.reason);
  });
  window.addEventListener('error', (event) => {
    tryRecoverStaleChunkError(event.error ?? event.message ?? '', event.filename);
  });
}
